import { PropsWithChildren, useMemo } from 'react';
import {
  MutationKey,
  QueryKey,
  useIsFetching,
  useIsMutating,
  useQueryClient,
} from '@tanstack/react-query';

import { Box } from '../Box';

import { CenteredLoader } from '../Loader';

export type ReactQueryLoaderProps = {
  fallbackKeys: Record<string, MutationKey | QueryKey>;
  fallback?: (label: string) => React.ReactNode;
};

export const ReactQueryLoader = ({
  children,
  fallbackKeys,
  fallback,
}: PropsWithChildren<ReactQueryLoaderProps>) => {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const queryClient = useQueryClient();

  const { showLoader, loadingLabel } = useMemo(() => {
    if (isFetching || isMutating) {
      const mutatingOrFetching = Object.entries(fallbackKeys).find(
        ([_, keys]) => {
          const isMutating = queryClient.isMutating({ mutationKey: keys });
          const isFetching = queryClient.isFetching({ queryKey: keys });

          return isMutating || isFetching;
        }
      );

      return {
        loadingLabel: mutatingOrFetching ? mutatingOrFetching[0] : '',
        showLoader: !!mutatingOrFetching,
      };
    }

    return {
      loadingLabel: '',
      showLoader: false,
    };
  }, [isFetching, isMutating, fallbackKeys, queryClient]);

  return (
    <>
      <Box sx={{ display: showLoader ? 'block' : 'none' }}>
        {fallback?.(loadingLabel) ?? <CenteredLoader label={loadingLabel} />}
      </Box>
      <Box
        data-testid="react-query-loader"
        sx={{
          display: !showLoader ? 'flex' : 'none',
          height: '100%',
          width: '100%',
        }}
      >
        {children}
      </Box>
    </>
  );
};
